import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTrip } from '../lib/tripContext.jsx';
import { useSession } from '../lib/sessionContext.jsx';
import { useTestMode } from '../lib/testModeContext.jsx';
import { fetchTransmissionsLog } from '../lib/transmissionsApi.js';

// Per-player aggregated log of every Wilman moment. Replaying matters
// most for headliner verdicts and airport moments — the trip's emotional
// peaks. Pagination kicks in past 30 entries to keep the DOM light on
// older iPhones.

const PAGE_SIZE = 30;

const FILTER_CHIPS = [
  { id: 'all', label: 'All' },
  { id: 'audio', label: 'Audio only' },
  { id: 'transmission', label: 'Transmissions' },
  { id: 'verdict', label: 'Verdicts' },
  { id: 'brief', label: 'Daily briefs' },
  { id: 'narration', label: 'Narration' },
  { id: 'airport-moment', label: 'Airport moments' },
];

const TYPE_LABEL = {
  transmission: 'Transmission',
  verdict: 'Verdict',
  brief: 'Daily brief',
  narration: 'Narration',
  airport_moment: 'Airport moment',
};

export default function TransmissionsLog() {
  const { tripId } = useTrip();
  const session = useSession();
  const testMode = useTestMode();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [order, setOrder] = useState('desc'); // newest-first
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await fetchTransmissionsLog({ tripId, testMode: !!testMode?.active });
      setData(res);
    } catch (err) {
      if (err.status === 401) {
        // Auth gate — surfaced via the visible "sign-in required" card.
        setData(null);
      } else {
        setError(err.message || String(err));
      }
    } finally {
      setLoading(false);
    }
  }, [tripId, testMode?.active]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    const list = data?.entries || [];
    let f;
    if (filter === 'all') f = list;
    else if (filter === 'audio') f = list.filter((e) => !!e.audio_url);
    else f = list.filter((e) => e.category === filter);
    if (order === 'asc') {
      return [...f].sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));
    }
    return f;
  }, [data?.entries, filter, order]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;

  const meId = session?.role === 'player' ? session.recipient_id : null;

  return (
    <div className="mx-auto w-full max-w-screen-sm space-y-4 p-4">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          archive · per-player
        </p>
        <h1 className="mt-1 font-serif text-2xl">Transmissions Log</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          Every Wilman moment from this trip — replayable, filterable, sorted by time.
        </p>
      </header>

      {!session.loading && !meId && (
        <div className="card border-copper/40 bg-copper/5 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-wider text-copper-dark">
            sign-in required
          </p>
          <p className="mt-1 text-sm text-fg-secondary">
            Open the app from your briefing-email link to see your personalized log.
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        {FILTER_CHIPS.map((c) => {
          const selected = filter === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                setFilter(c.id);
                setVisibleCount(PAGE_SIZE);
              }}
              className={[
                'rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider transition',
                selected
                  ? 'border-action bg-action text-action-fg'
                  : 'border-border-tertiary bg-bg-secondary text-fg-secondary hover:bg-bg-tertiary',
              ].join(' ')}
            >
              {c.label}
            </button>
          );
        })}
        <span aria-hidden className="flex-1" />
        <button
          type="button"
          onClick={() => setOrder((o) => (o === 'desc' ? 'asc' : 'desc'))}
          className="rounded-full border border-border-tertiary bg-bg-secondary px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-fg-secondary hover:bg-bg-tertiary"
          aria-label={`Sort ${order === 'desc' ? 'oldest first' : 'newest first'}`}
        >
          {order === 'desc' ? 'newest ↓' : 'oldest ↑'}
        </button>
      </div>

      {loading && (
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          …loading log
        </p>
      )}

      {!loading && filtered.length === 0 && meId && (
        <div className="card px-4 py-6 text-center font-serif italic text-fg-secondary">
          {filter === 'all'
            ? 'No transmissions yet. The trip starts on May 26.'
            : 'Nothing matching that filter.'}
        </div>
      )}

      <ul className="space-y-3">
        {visible.map((entry) => (
          <Entry key={entry.id} entry={entry} />
        ))}
      </ul>

      {hasMore && (
        <button
          type="button"
          onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
          className="w-full rounded-md border border-border-tertiary bg-bg-secondary px-3 py-2 font-mono text-xs uppercase tracking-wider text-fg-secondary hover:bg-bg-tertiary"
        >
          show {Math.min(PAGE_SIZE, filtered.length - visibleCount)} more · {filtered.length} total
        </button>
      )}
    </div>
  );
}

function Entry({ entry }) {
  const [expanded, setExpanded] = useState(false);
  const isVerdict = entry.type === 'verdict';
  const isRejected = isVerdict && entry.metadata?.verified === false;

  return (
    <li
      className={[
        'card overflow-hidden',
        isRejected ? 'border-danger/30' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="flex items-baseline justify-between gap-2 border-b border-border-tertiary bg-bg-secondary px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            {TYPE_LABEL[entry.type] || entry.type}
            {entry.metadata?.fired_reason && entry.metadata.fired_reason !== 'manual' && (
              <span className="ml-1 normal-case tracking-normal text-fg-tertiary">
                · {entry.metadata.fired_reason}
              </span>
            )}
          </p>
          <p className="font-serif text-base leading-tight text-fg-primary">{entry.title}</p>
        </div>
        <p className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          {formatTime(entry.timestamp)}
        </p>
      </header>

      <div className="space-y-2 px-3 py-2">
        {isVerdict && (
          <div className="flex flex-wrap items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider">
            <span
              className={[
                'rounded-full border px-2 py-0.5',
                entry.metadata?.verified
                  ? 'border-success/40 bg-success/10 text-success'
                  : 'border-danger/40 bg-danger/10 text-danger',
              ].join(' ')}
            >
              {entry.metadata?.verified ? 'verified' : 'rejected'}
            </span>
            {entry.metadata?.points != null && (
              <span className="rounded-full border border-border-tertiary bg-bg-primary px-2 py-0.5 text-fg-secondary">
                {entry.metadata.points} pts
              </span>
            )}
            {entry.metadata?.original_points != null &&
              entry.metadata.original_points !== entry.metadata.points && (
                <span className="rounded-full border border-copper/40 bg-copper/10 px-2 py-0.5 text-copper-dark">
                  override · was {entry.metadata.original_points}
                </span>
              )}
            {entry.metadata?.confidence != null && (
              <span className="rounded-full border border-border-tertiary bg-bg-primary px-2 py-0.5 text-fg-secondary">
                {Math.round(entry.metadata.confidence * 100)}% conf
              </span>
            )}
          </div>
        )}

        {entry.body && (
          <p
            className={[
              'whitespace-pre-wrap font-serif text-sm leading-relaxed text-fg-secondary',
              expanded ? '' : 'line-clamp-3',
            ].join(' ')}
          >
            {/* Strip SSML tags + audio direction markers for the
                preview — Wilman text often contains <break/> and
                [deadpan] that look strange in plain text. */}
            {cleanForDisplay(entry.body)}
          </p>
        )}

        {entry.body && entry.body.length > 200 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="font-mono text-[10px] uppercase tracking-wider text-action underline-offset-4 hover:underline"
          >
            {expanded ? 'collapse' : 'expand'}
          </button>
        )}

        {isVerdict && entry.metadata?.photo_url && (
          <img
            src={entry.metadata.photo_url}
            alt={entry.metadata?.challenge_id || 'submission'}
            className="max-h-48 w-full rounded-md border border-border-tertiary object-cover bg-black"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        )}

        {entry.audio_url && (
          <audio
            controls
            preload="none"
            src={entry.audio_url}
            className="w-full"
          >
            Your browser does not support the audio element.
          </audio>
        )}
      </div>
    </li>
  );
}

function cleanForDisplay(text) {
  if (!text) return '';
  return text
    .replace(/<break\s+time="[^"]*"\s*\/?>/gi, ' ')
    .replace(/\[(deadpan|scoffs|muttering|sigh|sighs|chuckles|whispers|softly|loudly|sarcastic|amused|annoyed|resigned|pause)\]/gi, '')
    .replace(/\s+([.,;!?])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' · ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
